import React from "react";
import { Box, Chip, Input, Tooltip } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { MAIL_PLACEHOLDER_TYPE_EMAIL } from "api/mailTemplates/MailTemplate.typed";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import { SDelete } from "../../../../styles/StyledMUI";
import { colorTokens } from "../../../../styles/globalStyles";
import {
  mailPlaceholderDragOverHandler,
  mailPlaceholderKeyFromDataTransfer,
} from "../mailEditorDnD";
import { mailPlaceholderKeyInDefinitionList } from "../mailPlaceholderContext";
import {
  MAIL_ENVELOPE_CONTROL_HEIGHT_PX,
  mailEnvelopeRecipientDisplayLabel,
  mailPlaceholderTooltip,
} from "../mailEditorShared";
import { mailPhToken, parseMailPlaceholderKeyFromToken } from "../mailLineParts";

type MailRecipientEditorProps = {
  list: string[];
  onAdd?: (v: string) => void;
  onRemove?: (index: number) => void;
  readOnly?: boolean;
  placeholderDropCatalog?: MailPlaceholderDefinition[];
  placeholderDropAllowedDefinitions?: MailPlaceholderDefinition[];
  valueLabelCatalog?: MailPlaceholderDefinition[];
  rowHeightPx?: number;
  onFieldFocus?: () => void;
};

export default function MailRecipientEditor(props: MailRecipientEditorProps) {
  const {
    list,
    onAdd,
    onRemove,
    readOnly = false,
    placeholderDropCatalog,
    placeholderDropAllowedDefinitions,
    valueLabelCatalog,
    rowHeightPx = MAIL_ENVELOPE_CONTROL_HEIGHT_PX,
    onFieldFocus,
  } = props;
  const canEdit = !readOnly && onAdd != null && onRemove != null;
  const [value, setValue] = React.useState<string>("");
  const textInputRef = React.useRef<HTMLInputElement | null>(null);

  const recipientDropAllowedDefinitions = React.useMemo(() => {
    if (placeholderDropAllowedDefinitions === undefined) {
      return undefined;
    }

    return placeholderDropAllowedDefinitions.filter((d) => d.type === MAIL_PLACEHOLDER_TYPE_EMAIL);
  }, [placeholderDropAllowedDefinitions]);

  const inputDropProps = React.useMemo(() => {
    if (!placeholderDropCatalog || recipientDropAllowedDefinitions === undefined || !onAdd) {
      return undefined;
    }

    return {
      onDragOver: mailPlaceholderDragOverHandler,
      onDrop: (e: React.DragEvent<HTMLInputElement>) => {
        e.preventDefault();

        const key = mailPlaceholderKeyFromDataTransfer(e.dataTransfer);

        if (!key) {
          return;
        }

        if (!mailPlaceholderKeyInDefinitionList(key, recipientDropAllowedDefinitions)) {
          return;
        }

        const token = mailPhToken(key);

        if (list.includes(token)) {
          return;
        }

        onAdd(token);
      },
    };
  }, [list, onAdd, placeholderDropCatalog, recipientDropAllowedDefinitions]);

  const chipLabel = React.useCallback(
    (raw: string) => mailEnvelopeRecipientDisplayLabel(raw, valueLabelCatalog),
    [valueLabelCatalog],
  );

  const chipTooltip = React.useCallback(
    (raw: string) => {
      const key = parseMailPlaceholderKeyFromToken(raw.trim());

      if (key && valueLabelCatalog && valueLabelCatalog.length > 0) {
        const def = valueLabelCatalog.find((d) => d.key === key);

        if (def) {
          return mailPlaceholderTooltip(def);
        }
      }

      return chipLabel(raw);
    },
    [chipLabel, valueLabelCatalog],
  );

  const commitAdd = React.useCallback(() => {
    const v = value
      .trim()
      .replace(/[,;]+$/u, "")
      .trim();

    if (v && onAdd) {
      if (!list.includes(v)) {
        onAdd(v);
      }

      setValue("");
    }
  }, [list, onAdd, value]);

  const chipInnerH = Math.max(22, rowHeightPx - 10);

  const chipSx = {
    flex: "0 0 auto",
    maxWidth: "100%",
    height: chipInnerH,
    minHeight: chipInnerH,
    boxSizing: "border-box" as const,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
      fontSize: 11,
      px: 0.5,
    },
  };

  const fieldShellSx = {
    position: "relative" as const,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: rowHeightPx,
    boxSizing: "border-box" as const,
    pl: 0.75,
    pr: 0.35,
    py: 0.35,
    gap: 0.5,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    backgroundColor: colorTokens.bg.surfaceMuted,
    color: colorTokens.text.primary,
    borderRadius: "8px",
    border: `1px solid ${colorTokens.border.strong}`,
    "&:hover": {
      borderColor: colorTokens.border.focus,
    },
    "&:focus-within": {
      borderColor: colorTokens.border.focus,
    },
    ...(canEdit ? {} : { opacity: 0.85 }),
  };

  const plainInputSx = {
    flex: "1 1 120px",
    minWidth: 120,
    minHeight: chipInnerH,
    bgcolor: "transparent",
    boxShadow: "none",
    border: "none",
    "--Input-focusedHighlight": "transparent",
    "--Input-paddingInline": "6px",
    "--Input-minHeight": `${chipInnerH}px`,
    "&::before": { display: "none" },
    "&::after": { display: "none" },
    "&:hover": { bgcolor: "transparent" },
    "& input": {
      fontSize: 13,
      py: 0,
      color: colorTokens.text.primary,
    },
    "& input::placeholder": {
      color: colorTokens.text.muted,
      opacity: 1,
    },
  };

  return (
    <Box
      sx={fieldShellSx}
      onFocusCapture={() => onFieldFocus?.()}
      onMouseDown={(e) => {
        if (!canEdit) {
          return;
        }

        const t = e.target as HTMLElement;

        if (t.closest("button") || t.closest('[role="button"]')) {
          return;
        }

        if (t.closest("input")) {
          return;
        }

        requestAnimationFrame(() => textInputRef.current?.focus());
      }}
    >
      {list.map((item, idx) => (
        <Tooltip key={`${item}-${idx}`} title={chipTooltip(item)}>
          <Chip
            variant="soft"
            sx={chipSx}
            endDecorator={
              canEdit && onRemove ? (
                <IconButtonCTA
                  size="sm"
                  aria-label="Retirer le destinataire"
                  ctaVariant="danger"
                  sx={{ pointerEvents: "auto" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(idx);
                  }}
                >
                  <SDelete sx={{ fontSize: 16 }} />
                </IconButtonCTA>
              ) : undefined
            }
          >
            {chipLabel(item)}
          </Chip>
        </Tooltip>
      ))}

      {canEdit ? (
        <Input
          variant="plain"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitAdd();
            }
          }}
          sx={plainInputSx}
          slotProps={{
            input: {
              ...inputDropProps,
              ref: textInputRef,
              onFocus: () => onFieldFocus?.(),
              onBlur: () => {
                commitAdd();
              },
            },
          }}
        />
      ) : list.length === 0 ? (
        <DarkTypography level="body-sm" colorVariant="muted" sx={{ pl: 0.5 }}>
          —
        </DarkTypography>
      ) : null}
    </Box>
  );
}
